import {
  zionGeologyBriefingPhoto,
  zionFinalSelfieWithSamanthaPhoto,
  zionNightSkyPhoto,
  zionPlaneViewPhoto,
  zionRockPassagePhoto,
  zionScoutLookoutCrewPhoto,
  zionScoutLookoutViewPhoto,
  zionScoutsLookoutStartPhoto,
  zionSlotCanyonCrewPhoto,
  zionSunriseCliffsPhoto,
  zionSwitchbackGroupPhoto,
  zionTrailEndsCrewPhoto,
  zionTrailEndsRestPhoto,
  zionChasingSunsetPhoto,
  zionWatchmanOverlookPhoto,
  zionWatchmanSunsetPhoto,
  zionWatchmanTrailheadFinishPhoto,
  zionWalkBackToVanPhoto,
  zionTrailEndsRiverPhoto,
  zionWaltersWigglesPhoto,
  zionWellEarnedDescentPhoto,
} from './zionHikeGallery';

const sourceLines = [
  'Zion National Park',
  'Two Days. Countless Memories.',
  'November 30 – December 1, 2024',
  'There are some places that earn a spot on your bucket list long before you ever set foot there.',
  'For me, Zion National Park was one of them.',
  'Towering sandstone cliffs.',
  'Narrow slot canyons.',
  'The famous Angels Landing trail.',
  'For years I\'d seen photographs and videos of Zion, always thinking, Someday.',
  'When we began planning our AWS re:Invent trip to Las Vegas, we realized that "someday" was only a few hours away.',
  'Rather than spending another day inside the conference, we decided to do something we\'d remember long after we forgot which breakout sessions we attended.',
  'We hired a local guide, rented a twelve-passenger van, and pointed it toward southern Utah.',
  'Joining the adventure were Marcus and Ash\'Leigh, Brandon A., Brandon L., Jasmine and Steve, and our guide, Samantha.',
  'What followed wasn\'t just a hike.',
  'It became a two-day journey that reminded us why national parks inspire so many people to return again and again.',
  'The first day was about anticipation.',
  'The drive from Las Vegas through the Mojave Desert.',
  'Our first evening in Springdale.',
  'Dinner with the team.',
  'A quiet walk beneath an incredible night sky.',
  'The feeling that something unforgettable was waiting just beyond the sunrise.',
  'The second day delivered on that promise.',
  'Three very different hikes.',
  'Scout Lookout high above Zion Canyon.',
  'The Riverside Walk to the gateway of The Narrows.',
  'A sunset climb to the Watchman overlook.',
  'Along the way, Samantha transformed a beautiful national park into a living story, helping us understand not only what we were seeing, but how millions of years of geology, water, and time had shaped it.',
  'By the end of the trip, we\'d seen Zion from high above the canyon, beside the Virgin River, and beneath one of the most spectacular sunsets we\'d ever experienced.',
  'Some places live up to the hype.',
  'Zion somehow exceeded it.',
  '',
  'Day One',
  'The Road to Zion',
  'Our adventure began before we ever reached the park.',
  'On the flight into Las Vegas, Zion appeared outside the plane window, its sandstone cliffs and winding canyons rising from the desert far below.',
  'That first glimpse made the place feel real before the wheels had even touched down.',
  'We landed in Las Vegas on the afternoon of November 30th and headed straight to the Enterprise rental center to pick up our transportation for the next two days.',
  'The plan seemed simple.',
  'Reserve a twelve-passenger van.',
  'Load everyone\'s luggage.',
  'Drive three hours to Springdale.',
  'Instead, our first van came with a small problem.',
  'It didn\'t have license plates.',
  'Somehow it had made it through the rental process without anyone noticing.',
  'After a few laughs and a little waiting, Enterprise swapped us into another van and we were finally on our way.',
  'Leaving the lights of Las Vegas behind, the desert slowly took over.',
  'The farther we drove, the quieter things became.',
  'Three hours doesn\'t sound particularly long, but the drive felt much longer than any of us expected. Maybe it was the slower pace of the large van. Maybe it was simply the anticipation of finally reaching Zion after years of wanting to visit.',
  'Either way, nobody seemed to mind.',
  'The conversation flowed easily as the miles passed beneath us.',
  'By the time we reached Springdale, the sun had long since disappeared behind the cliffs.',
  'From the ground, we couldn\'t actually see Zion yet.',
  'Only enormous silhouettes rising into the night sky.',
  'That almost made it more exciting.',
  'Tomorrow morning we\'d finally meet them.',
  '',
  'Dinner at Switchback Grille',
  'We checked into the Cliffrose Springdale, unpacked our bags, and headed to dinner at Switchback Grille.',
  'After several hours in the van, sitting around a table together felt like the perfect way to begin the trip.',
  'The steaks were excellent.',
  'The conversation was even better.',
  'Looking back, these dinners have become just as much a part of our adventures as the places we visit.',
  'It\'s where everyone slows down.',
  'Stories get shared.',
  'Plans get adjusted.',
  'And excitement for the next day begins to build.',
  '',
  'Last-Minute Preparations',
  'After dinner we made one final stop at a nearby convenience store.',
  'Water.',
  'Sports drinks.',
  'Trail mix.',
  'Protein bars.',
  'Beef jerky.',
  'Everything we\'d need for a full day on the trails.',
  'Back at the hotel, most of the group settled in for the night.',
  '',
  'Looking Up',
  'Before calling it a night, Marcus, Brandon A., Brandon L., and I wandered behind the hotel.',
  'Away from the lights.',
  'Away from the parking lot.',
  'The goal was simple.',
  'See the stars.',
  'The hotel\'s fire pits had already gone out, leaving the area surprisingly dark.',
  'Standing there, surrounded by silence, we looked up into one of the clearest night skies any of us had seen in years.',
  'Then someone spotted movement.',
  'A small group of mule deer quietly walked through the property as if we weren\'t even there.',
  'They paused.',
  'We paused.',
  'For a few moments we simply shared the space before they disappeared back into the darkness.',
  'It was the perfect ending to our first day.',
  'Eventually, Brandon A., Brandon L., and Marcus headed inside and went to bed.',
  '',
  'One Last Walk',
  'Before I could do the same, I realized I\'d forgotten something.',
  'I quietly walked through the streets of Springdale to Sol Foods by myself.',
  'To this day I honestly can\'t remember what I went to buy.',
  'Whatever it was clearly wasn\'t very important.',
  'What I do remember is how peaceful the town felt.',
  'Almost no traffic.',
  'Cool desert air.',
  'The cliffs standing silently in the darkness above me.',
  'It was one of those quiet moments that somehow becomes one of the memories you keep.',
  'When I returned to the hotel, I finally called it a night and went to bed.',
  'Tomorrow we\'d finally see Zion.',
  '',
  'Day Two',
  'First Light',
  'The alarm came early.',
  'Six o\'clock.',
  'Thanks to the time difference from the East Coast, waking up wasn\'t nearly as difficult as it might have been.',
  'One by one everyone got ready, packed their rooms, and loaded luggage back into the van.',
  'As the first sunlight reached the canyon walls surrounding Springdale, we caught our first real glimpse of Zion.',
  'The silhouettes from the night before had transformed into towering cliffs glowing in the morning light.',
  'Even standing in the hotel parking lot, it was obvious this place was special.',
  'After breakfast we checked out of the hotel and drove all of one minute before reaching the entrance to Zion National Park.',
  '',
  'Meeting Samantha',
  'After paying the entrance fee, we parked near the Visitor Center where we were supposed to meet our guide.',
  'Finding her wasn\'t quite as easy as we expected.',
  'A few text messages.',
  'A quick phone call.',
  'Eventually we spotted her in another section of the parking lot.',
  'Introductions were made, and Samantha opened the trunk of her car.',
  'Inside was everything we\'d need for the day.',
  'Backpacks.',
  'Trekking poles.',
  'Water.',
  'Drinks.',
  'Snacks.',
  'As everyone selected gear and adjusted straps, the day suddenly became real.',
  'We weren\'t tourists anymore.',
  'We were hikers.',
  '',
  'The First Lesson',
  'Before we reached the shuttle stop, Samantha paused beside the large three-dimensional relief map of Zion National Park.',
  'Most visitors probably walked right past it.',
  'We didn\'t.',
  'Gathered around the map, she traced the path of the Virgin River as it wound through the canyon and explained how millions of years of uplift, erosion, and flowing water had created the landscape surrounding us.',
  'She pointed out where we were standing.',
  'Then she showed us where we were headed.',
  'Our morning would begin with the climb to Scout Lookout.',
  'After a picnic lunch we\'d continue deeper into the canyon for the Riverside Walk to the entrance of The Narrows.',
  'If daylight cooperated, we\'d finish the afternoon on the Watchman Trail before beginning the drive back to Las Vegas.',
  'Looking down at the relief map, the day suddenly felt much bigger than three hikes.',
  'We weren\'t simply following trails.',
  'We were about to experience Zion from three completely different perspectives.',
  '',
  'Riding Into Zion',
  'The shuttle arrived a few minutes later.',
  'As we climbed aboard, conversations naturally became quieter.',
  'Nobody wanted to miss the view.',
  'With every stop, the canyon seemed to grow taller.',
  'Massive sandstone walls rose on both sides of us while the Virgin River wound through the valley below.',
  'As we made our way deeper into the park, Samantha continued introducing us to Zion.',
  'She pointed out the Court of the Patriarchs as we passed and explained how many of the park\'s most recognizable formations had been given biblical names inspired by their immense scale.',
  'She talked about the Navajo Sandstone surrounding us and how these towering cliffs had once been ancient sand dunes before millions of years transformed them into the landscape outside our windows.',
  'The more she explained, the more remarkable Zion became.',
  'This wasn\'t simply a beautiful place.',
  'It was a place with an incredible story.',
  'A few minutes later the shuttle doors opened.',
  'It was time to start climbing.',
  '',
  'Above the Canyon',
  'The shuttle doors slid open, and we stepped onto the trail.',
  'One last adjustment to a backpack.',
  'A quick sip of water.',
  'Trekking poles in hand.',
  'After months of planning, we were finally hiking in Zion National Park.',
  'Leaving the shuttle stop behind, we crossed the road and stepped onto a bridge spanning the Virgin River.',
  'The bridge felt like a gateway.',
  'Behind us were the Visitor Center, shuttle buses, and the activity of the park.',
  'Ahead of us was Zion.',
  'Below, the Virgin River quietly flowed through the canyon, the same river Samantha had traced across the relief map only a few minutes earlier. Beyond the bridge, the trail disappeared between towering sandstone walls that seemed almost impossible in scale.',
  'One of the first things that surprised me was the trail itself.',
  'It wasn\'t the rugged mountain path I had imagined.',
  'The route was paved from start to finish and exceptionally well maintained. Through this lower section it was comfortably wide, allowing several people to walk side by side while still leaving room for hikers traveling in the opposite direction.',
  'The first part of the hike was surprisingly peaceful.',
  'For a trail this well known, I expected a steady stream of people.',
  'Instead, it often felt like we had the canyon almost entirely to ourselves.',
  'Occasionally another group would pass heading back toward the shuttle stop, but for long stretches it was just our team walking together, talking, and taking in our surroundings.',
  'It was the perfect way to ease into the day.',
  'Gradually the trail began climbing.',
  'Long, gentle switchbacks carried us higher above the valley floor, and almost without realizing it, we found ourselves stopping at the end of nearly every switchback.',
  'Not because we needed the break.',
  'Because every turn offered a different view.',
  'As we gained elevation, the perspective changed almost constantly.',
  'The Virgin River drifted farther below.',
  'The road became smaller.',
  'The shuttle buses looked like miniature toys moving through the canyon.',
  'At the same time, the morning sun continued its climb across the sky.',
  'With each passing minute, another section of the sandstone cliffs caught the light while neighboring walls remained in shadow. Deep reds slowly gave way to brilliant oranges and warm golds, making it feel as though the canyon was changing before our eyes.',
  'It was impossible not to stop for photographs.',
  'Every switchback offered a view unlike the one before it.',
  'The higher we climbed, the more we realized this wasn\'t simply a hike.',
  'It was an ever-changing panorama that rewarded every few hundred feet with an entirely new perspective.',
  '',
  'A Change of Pace',
  'Eventually the climb eased.',
  'The trail flattened as it entered a broader section of the canyon, giving everyone a chance to catch their breath before the final ascent.',
  'This part of the hike had a completely different personality.',
  'Instead of focusing on elevation, we found ourselves slowing down and taking a closer look at the world around us.',
  'The sandstone walls surrounding the trail were filled with natural alcoves and carved-out openings shaped by centuries of wind and water. Several invited us to climb inside, scramble over smooth rock, and discover new perspectives hidden just a few steps off the trail.',
  'With the pace slowing, we had time to notice details that were easy to miss while climbing.',
  'The warm reds, oranges, and creams of the sandstone were interrupted by long dark brown and black streaks running down the canyon walls, almost as if someone had painted them onto the rock.',
  'Samantha smiled when she saw us studying them.',
  '"That\'s called desert varnish."',
  'Despite the name, it isn\'t paint at all.',
  'She explained that the dark coating forms over thousands of years as clay and minerals accumulate on the rock\'s surface, and that scientists believe microscopic organisms help create it. Something that looked completely lifeless from a distance had actually been shaped, in part, by microscopic life over countless generations.',
  'It was one of those fascinating facts that instantly changed how we looked at the canyon.',
  'From that point on, we couldn\'t stop noticing it.',
  'Every few minutes someone wandered into another opening, found another interesting rock formation, or paused to admire another stretch of desert varnish tracing its way down the sandstone.',
  'It was impossible to rush through this section.',
  'The trail encouraged curiosity.',
  '',
  '',
  'Walter\'s Wiggles',
  'Eventually the trail pointed skyward once again.',
  'Ahead of us stretched one of Zion\'s most recognizable landmarks.',
  'Walter\'s Wiggles.',
  'Twenty-one tight switchbacks stacked one directly above another, climbing relentlessly toward Scout Lookout.',
  'Unlike the broad, open sections lower on the mountain, the trail narrowed considerably here. The wide pavement gave way to a more intimate climb as hikers naturally settled into a single-file rhythm.',
  'It was also where we encountered the most people we\'d seen all morning.',
  'Everyone climbed at their own pace, occasionally pausing to catch their breath or admire the ever-expanding views behind them.',
  'Despite its popularity, the trail never felt congested.',
  'It simply felt like people sharing the same incredible experience.',
  'One switchback became another.',
  'Then another.',
  'Conversation naturally became less frequent.',
  'Not because anyone was struggling.',
  'Simply because everyone was focused on the climb.',
  'Every glance over a shoulder revealed a view that somehow looked even better than the one before.',
  'Eventually the final switchback disappeared behind us.',
  'One last short climb remained.',
  '',
  'Scout Lookout',
  'The trail opened onto a broad sandstone overlook, and suddenly the entire canyon spread out beneath us.',
  'We had made it.',
  'Scout Lookout.',
  'There were already plenty of hikers enjoying the overlook, resting after the climb or watching others prepare to continue onto the famous chain section of Angels Landing.',
  'What surprised me was how spacious it felt.',
  'Even with dozens of people taking photographs, sitting on the sandstone, or simply soaking in the view, there was plenty of room to spread out.',
  'It never felt crowded.',
  'It felt shared.',
  'Everyone had made the same climb.',
  'Everyone had earned the same view.',
  'We wandered to the edge of the overlook, taking in the sweeping panorama of Zion Canyon stretching for miles in every direction.',
  'Then Samantha pointed toward the sky.',
  'High above the canyon, several California condors circled effortlessly on the morning thermals.',
  'With wingspans approaching ten feet, they seemed almost unreal against the towering sandstone cliffs.',
  'Watching one of North America\'s rarest birds soar above one of America\'s most spectacular landscapes made the moment even more memorable.',
  'We spent a long time taking photographs, enjoying the accomplishment, and simply appreciating where we were.',
  'Twenty-four hours earlier, we had been boarding a flight to Las Vegas.',
  'Now we were standing high above Zion Canyon, watching condors drift silently across an endless blue sky.',
  'Moments like these are why we take the trip.',
  'Not just to visit remarkable places.',
  'But to experience them together.',
  '',
  'A Well-Earned Descent',
  'After spending plenty of time taking photographs and soaking in the views, we reluctantly began the hike back down.',
  'The return trip always feels different.',
  'The climb that demanded your attention on the way up becomes almost effortless on the way down.',
  'Gravity was finally working in our favor.',
  'By now, we were all starting to feel hungry, and our pace was noticeably quicker than it had been that morning.',
  'Even so, Zion had one more gift to offer before lunch.',
  'The canyon looked completely different.',
  'The sun had climbed much higher into the sky, illuminating cliffs that had been hidden in shadow only a few hours earlier. Colors continued to shift across the sandstone, and viewpoints that had looked one way during the ascent now offered an entirely different perspective on the descent.',
  'It was a reminder that landscapes like Zion are never quite the same twice.',
  'By the time we crossed the Virgin River and reached the shuttle stop, we had worked up quite an appetite.',
  'What we didn\'t realize was that Samantha had one more surprise waiting for us.',
  '',
  'Lunch, Zion Style',
  'Just across the street from the trailhead, a picnic table sat waiting for us.',
  'As we dropped our backpacks and found seats around the table, Samantha quietly began unpacking her own.',
  'At first it didn\'t seem unusual.',
  'Then she kept pulling things out.',
  'And pulling more things out.',
  'None of us could figure out how everything fit inside the backpack she\'d carried all the way to Scout Lookout.',
  'Within a few minutes, the picnic table had been transformed into an incredible homemade charcuterie spread.',
  'Fresh meats.',
  'Cheeses.',
  'Crackers.',
  'Fruit.',
  'Nuts.',
  'And one item that I still remember today.',
  'A local spicy hummus.',
  'I\'ll admit, I\'m not usually much of a hummus person.',
  'But this was different.',
  'It had just enough heat, incredible flavor, and somehow tasted even better after a morning on the trail.',
  'I still think about it.',
  'Just when we thought she\'d finished, Samantha reached into her pack again.',
  'Out came a thermos of hot water.',
  'Soon everyone was warming their hands around cups of hot tea or hot apple cider.',
  'On a cool December afternoon in Zion, it couldn\'t have been a more perfect ending to the morning.',
  'As we sat together sharing lunch, looking back toward the towering cliffs we\'d climbed only a short time earlier, it was hard not to appreciate how much thought Samantha had put into every part of the day.',
  'She hadn\'t simply guided us through Zion.',
  'She had created an experience.',
  'It was one of those small acts of hospitality that stays with you long after you\'ve forgotten exactly how many miles you hiked.',
  '',
  '',
  '',
  'Along the Virgin River',
  'After spending a little more time enjoying lunch, we gathered our gear and made our way back to the shuttle stop.',
  'This time we stayed on as the shuttle continued farther into Zion Canyon, eventually reaching its final destination: the Temple of Sinawava.',
  'As soon as we stepped off the shuttle, it was obvious this hike would be completely different from the morning.',
  'Gone were the climbs and switchbacks.',
  'Instead, a wide, paved path followed the Virgin River deeper into the canyon.',
  'For the next mile, the river remained our constant companion, flowing quietly just to our left while towering sandstone walls rose almost straight overhead.',
  'The pace was slower.',
  'There was no elevation to conquer.',
  'No destination to race toward.',
  'Just an easy stroll through one of the most beautiful sections of Zion.',
  'Although it was already December, traces of winter still lingered.',
  'Light frost and patches of snow clung to shaded tree limbs along the riverbank, adding touches of white to a landscape otherwise filled with warm reds, oranges, and creams.',
  'The contrast made the canyon feel even more alive.',
  'One thing immediately stood out.',
  'There were far more people on this trail than we had encountered on the hike to Scout Lookout.',
  'That wasn\'t surprising.',
  'The Riverside Walk is accessible to almost everyone, making it one of Zion\'s most popular trails.',
  'What did catch our attention was what many of those hikers were wearing.',
  'Person after person walked past us dressed in chest-high waterproof waders, sturdy hiking boots, and carrying long wooden walking sticks.',
  'At first, it didn\'t make much sense.',
  'It looked less like a hiking trail and more like people preparing to go fly fishing.',
  'We smiled, exchanged a few puzzled looks, and continued down the path.',
  'The reason would become obvious soon enough.',
  '',
  'Reading the Canyon',
  'Not far from the trailhead, Samantha stopped beside an interpretive display.',
  'Like the relief map that morning, it would have been easy to walk right past.',
  'Instead, it became another lesson that forever changed how I look at Zion.',
  'She explained that the cliffs surrounding us were only one chapter in a much larger geological story.',
  'Beginning at Bryce Canyon, layers of rock gradually step downward through Zion before eventually becoming some of the oldest exposed rock in the Grand Canyon. Together, the three parks form what geologists call the Grand Staircase.',
  'Standing in Zion suddenly felt like standing in the middle chapter of an enormous book written over hundreds of millions of years.',
  'Then Samantha explained something even more remarkable.',
  'The rain and snow that fall high above the canyon don\'t simply run off the surface.',
  'Much of that water slowly seeps into the porous Navajo Sandstone, beginning a journey that can take centuries.',
  'In some places, it may take nearly a thousand years for a drop of water to filter through the rock before emerging from the canyon walls below.',
  'A raindrop falling today might not complete its journey until long after we\'re gone.',
  'It was almost impossible to comprehend.',
  'The canyon wasn\'t just shaped by water flowing through it.',
  'It was still being shaped by water moving silently inside it.',
  '',
  'The Canyon Never Stops Changing',
  'As we continued deeper along the trail, Samantha pointed toward several large piles of broken sandstone resting at the base of the canyon walls.',
  'At first they looked like ordinary boulder fields.',
  'Then she asked us to look up.',
  'High above, fresh scars interrupted the otherwise smooth sandstone cliffs.',
  'It was easy to match the broken rock below with the missing sections above, almost like fitting together pieces of a giant puzzle.',
  'Some of the fallen blocks were enormous.',
  'The scale was hard to appreciate until you stood beside them.',
  'These weren\'t ancient events from millions of years ago.',
  'They were relatively recent reminders that Zion is still changing.',
  'Freeze-thaw cycles, gravity, and time continue reshaping the canyon, one rockfall at a time.',
  'It was fascinating to realize that the landscape surrounding us wasn\'t frozen in history.',
  'It was still evolving.',
  'Just very, very slowly.',
  '',
  'Where the Trail Ends',
  'Eventually the paved path came to an end.',
  'Ahead of us, the canyon narrowed dramatically as the towering sandstone walls closed in around the Virgin River.',
  'This was the beginning of The Narrows.',
  'Suddenly, all those hikers wearing chest-high waders and carrying long wooden walking sticks made perfect sense.',
  'There was no bridge.',
  'No trail.',
  'From this point forward, the Virgin River was the trail.',
  'One by one, hikers stepped directly into the cold water and slowly disappeared upstream between the narrowing canyon walls.',
  'It was unlike anything I\'d ever seen.',
  'We weren\'t equipped for that adventure.',
  'Without waders, there was only one place left to go.',
  'Carefully making our way down the riverbank, we climbed across a collection of boulders that rose just above the water\'s edge.',
  'Instead of immediately turning around, we spent a while exploring.',
  'Each rock offered a slightly different perspective of the river as it disappeared into The Narrows. We wandered along the shoreline, climbed onto larger boulders, and found a few perfect spots to stop and take photographs.',
  'It was peaceful.',
  'The sound of the river echoed softly between the canyon walls while hikers farther upstream gradually disappeared around the bend.',
  'Standing there, it was easy to understand why so many people consider this one of Zion\'s most iconic places.',
  'Even without walking into the river, it felt like we\'d reached the gateway to another world.',
  'Eventually, after one last look upstream and a few final photographs, we climbed back onto the trail and began the easy walk toward the shuttle.',
  'As we retraced our steps along the Virgin River, I couldn\'t help but think there was still one part of Zion left unexplored.',
  'Someday, I\'d come back.',
  'And next time...',
  'I\'d rent the waders.',
  '',
  'One Last Adventure',
  'The walk back along the Virgin River felt even more relaxed than the hike in.',
  'We had reached the end of the trail, spent time exploring the riverbank, and taken in our final views of The Narrows. There was no reason to hurry now.',
  'Before long, we were back aboard the shuttle, retracing our route through Zion Canyon toward the Visitor Center.',
  'It gave us one last chance to admire the cliffs from below.',
  'Places that had seemed impossibly high from the Riverside Walk now carried a different meaning after everything Samantha had taught us throughout the day. Instead of simply seeing beautiful rock formations, we found ourselves noticing layers of sandstone, weathered ledges, and wondering how the canyon had been shaped over millions of years.',
  'By the time we stepped off the shuttle, it felt like we\'d already packed a full day\'s worth of experiences into a single afternoon.',
  'Naturally, we couldn\'t leave without making one last stop inside the Visitor Center.',
  'Like every good national park gift shop, it was impossible to walk through without finding something to bring home. A few souvenirs made their way into backpacks, along with some drinks and snacks to give us enough energy for whatever came next.',
  'Outside, Samantha was waiting.',
  'Once everyone had regrouped, she smiled and pointed across the parking lot.',
  '"Our last hike starts right over there."',
  'The trailhead for the Watchman Trail was only a short walk away.',
  'It was hard to believe there was still another hike left after everything we\'d already done.',
  'Then Samantha added one more detail.',
  '"We\'ll need to pick up the pace a little."',
  'The sun was beginning its slow descent behind the canyon walls, and she wanted us to reach the overlook with enough time to enjoy the sunset before making our way back down in daylight.',
  'She assured us it would be worth it.',
  'By now, we didn\'t need convincing.',
  'Every recommendation she\'d made throughout the day had become one of our favorite memories.',
  'We tightened our backpack straps one last time, grabbed a final sip of water, and headed across the parking lot toward the trailhead.',
  'One last climb.',
  'One last perspective.',
  'One last unforgettable view.',
  '',
  'Chasing the Sunset',
  'Crossing the parking lot, it became immediately clear that the Watchman Trail would be different from the day\'s first two hikes.',
  'The wide, paved paths we\'d spent the day exploring were behind us.',
  'Although the trail was well maintained and easy to follow, it felt much more natural. Packed dirt gave way to broad stretches of sandstone, and in several places the route had been carved directly into the slickrock itself. The uneven surface demanded a little more attention to each step, especially since we were keeping a brisk pace to reach the overlook before sunset.',
  'As we climbed higher, another thought crossed my mind.',
  'Earlier that afternoon, Samantha had shown us the massive rockfalls scattered along the valley floor and pointed out the scars they had left high on the canyon walls. Now, as we crossed broad sandstone ledges and rounded overhangs, I found myself wondering if these were the kinds of formations those giant boulders had once broken away from.',
  'Looking at the smooth shelves of sandstone beneath our feet, it wasn\'t hard to imagine how thousands of years of freezing, thawing, and gravity could eventually separate an entire section of cliff from the canyon wall.',
  'That realization brought with it a familiar feeling.',
  'It was the same slight nervousness you sometimes get standing on the observation deck of a tall building or looking over the railing of a high bridge. I never felt unsafe because the trail was well maintained and Samantha led us confidently. Still, knowing what nature had done over thousands of years gave me a newfound respect for the landscape beneath my feet.',
  'There wasn\'t much time to dwell on it anyway.',
  'Samantha was setting a brisk pace, and with the sun sinking lower behind the canyon walls, we kept moving toward the overlook.',
  'Earlier that afternoon, we\'d admired these towering cliffs from beside the Virgin River.',
  'Now we were walking across them, seeing the canyon from an entirely different perspective.',
  'Sunset from the Watchman',
  'The climb to the overlook was a little over a mile and a half, and thanks to Samantha\'s steady pace, we reached the top with enough time to enjoy the destination instead of simply checking it off.',
  'There was no rush once we arrived.',
  'The overlook offered panoramic views in every direction, and we wandered around taking photographs from every angle we could find. Each few steps revealed a slightly different perspective of Zion Canyon, making it impossible to settle on a single favorite viewpoint.',
  'As the sun slowly slipped behind the canyon walls, the light transformed the landscape one final time.',
  'The brilliant reds and oranges of the sandstone softened into warm shades of gold before gradually giving way to shadow. The cliffs that had towered above us all day now stood quietly against an evening sky that seemed to change color by the minute.',
  'It was the perfect ending to a day that had shown us Zion from so many different perspectives.',
  'Eventually, the fading light reminded us it was time to head back.',
  'The descent was much easier than the climb, but we kept a steady pace, knowing darkness wasn\'t far behind. Before long we were back at the trailhead with daylight still to spare.',
  'Crossing the parking lot for the last time, we returned the backpacks, trekking poles, and other gear Samantha had loaned us that morning.',
  'After spending an entire day sharing her knowledge, guiding our hikes, and making sure every detail was taken care of, all that was left to do was thank her.',
  'She had given us far more than a guided hike.',
  'She had given us a completely different way to experience Zion.',
  'With one last goodbye, we climbed back into the van and pointed it toward Las Vegas.',
  'The excitement of the day slowly gave way to quiet conversation before, one by one, everyone else drifted off to sleep.',
  'The van became almost silent.',
  'Everyone, that is, except the driver.',
  'As the miles of desert highway disappeared beneath the headlights, I had plenty of time to reflect on the day we had just experienced.',
  'Three very different hikes.',
  'A guide who helped us see far more than trails and viewpoints.',
  'A national park that somehow looked completely different every few hours simply because we had changed our perspective.',
  'Some adventures end when you reach the destination.',
  'The best ones stay with you long after the drive home begins.',
  '',
  '',
];

const storyLines = sourceLines.map((line) => line.trim()).filter(Boolean);

const copyBetween = (startHeading, endHeading = '') => {
  const startIndex = storyLines.indexOf(startHeading);
  const endIndex = endHeading ? storyLines.indexOf(endHeading, startIndex + 1) : storyLines.length;
  return storyLines.slice(startIndex + 1, endIndex);
};

const sectionFromCopy = (heading, nextHeading = '') => ({
  heading,
  paragraphs: copyBetween(heading, nextHeading),
});

const sectionsFromCopy = (headings, pageEnd = '') => headings.map((heading, index) => (
  sectionFromCopy(heading, headings[index + 1] || pageEnd)
));

const dayOneHeadings = [
  'The Road to Zion',
  'Dinner at Switchback Grille',
  'Last-Minute Preparations',
  'Looking Up',
  'One Last Walk',
];

const dayOneSections = sectionsFromCopy(dayOneHeadings, 'Day Two').map((section, index) => {
  if (index === 0) {
    return {
      ...section,
      images: [{ ...zionPlaneViewPhoto, wide: true }],
      imageAfterParagraph: 2,
    };
  }

  if (section.heading === 'Looking Up') {
    return {
      ...section,
      images: [{ ...zionNightSkyPhoto, wide: true }],
      imageAfterParagraph: 6,
    };
  }

  if (section.heading === 'Dinner at Switchback Grille') {
    return {
      ...section,
      links: [
        {
          label: 'Cliffrose Springdale',
          href: 'https://cliffroselodge.com/',
        },
        {
          label: 'Switchback Grille',
          href: 'https://www.switchbackgrille.com/',
        },
      ],
    };
  }

  if (section.heading === 'One Last Walk') {
    return {
      ...section,
      links: [
        {
          label: 'Sol Foods',
          href: 'https://www.solfoods.com/',
        },
      ],
    };
  }

  return section;
});

const dayTwoHeadings = [
  'First Light',
  'Meeting Samantha',
  'The First Lesson',
  'Riding Into Zion',
  'Above the Canyon',
  'A Change of Pace',
  'Walter\'s Wiggles',
  'Scout Lookout',
  'A Well-Earned Descent',
  'Lunch, Zion Style',
  'Along the Virgin River',
  'Reading the Canyon',
  'The Canyon Never Stops Changing',
  'Where the Trail Ends',
  'One Last Adventure',
  'Chasing the Sunset',
  'Sunset from the Watchman',
];

const dayTwoSections = sectionsFromCopy(dayTwoHeadings).map((section) => {
  if (section.heading === 'First Light') {
    return {
      ...section,
      images: [{ ...zionSunriseCliffsPhoto, wide: true }],
      imageAfterParagraph: 5,
    };
  }

  if (section.heading === 'Above the Canyon') {
    return {
      ...section,
      imagePlacements: [
        {
          afterParagraph: 4,
          images: [{ ...zionScoutsLookoutStartPhoto, wide: true }],
        },
        {
          afterParagraph: 17,
          images: [{ ...zionGeologyBriefingPhoto, wide: true }],
        },
        {
          afterParagraph: 21,
          images: [{ ...zionSwitchbackGroupPhoto, wide: true }],
        },
      ],
    };
  }

  if (section.heading === 'A Change of Pace') {
    return {
      ...section,
      imagePlacements: [
        {
          afterParagraph: 4,
          images: [
            { ...zionRockPassagePhoto, wide: true },
            { ...zionSlotCanyonCrewPhoto, wide: true },
          ],
        },
      ],
    };
  }

  if (section.heading === 'Walter\'s Wiggles') {
    return {
      ...section,
      images: [{ ...zionWaltersWigglesPhoto, wide: true }],
      imageAfterParagraph: section.paragraphs.length - 1,
    };
  }

  if (section.heading === 'Scout Lookout') {
    return {
      ...section,
      images: [
        { ...zionScoutLookoutViewPhoto, wide: true },
        { ...zionScoutLookoutCrewPhoto, wide: true },
      ],
      imageAfterParagraph: section.paragraphs.length - 1,
    };
  }

  if (section.heading === 'A Well-Earned Descent') {
    return {
      ...section,
      images: [{ ...zionWellEarnedDescentPhoto, wide: true }],
      imageAfterParagraph: section.paragraphs.length - 1,
    };
  }

  if (section.heading === 'Where the Trail Ends') {
    return {
      ...section,
      images: [
        { ...zionTrailEndsRiverPhoto, wide: true },
        { ...zionTrailEndsCrewPhoto, wide: true },
        { ...zionTrailEndsRestPhoto, wide: true },
      ],
      imageAfterParagraph: section.paragraphs.length - 1,
    };
  }

  if (section.heading === 'Chasing the Sunset') {
    return {
      ...section,
      images: [{ ...zionChasingSunsetPhoto, wide: true }],
      imageAfterParagraph: 2,
    };
  }

  if (section.heading === 'Sunset from the Watchman') {
    return {
      ...section,
      imagePlacements: [
        {
          afterParagraph: 2,
          images: [
            { ...zionWatchmanOverlookPhoto, wide: true },
            { ...zionWatchmanSunsetPhoto, wide: true },
          ],
        },
        {
          afterParagraph: 7,
          images: [
            { ...zionWatchmanTrailheadFinishPhoto, wide: true },
            { ...zionWalkBackToVanPhoto, wide: true },
          ],
        },
        {
          afterParagraph: 10,
          images: [{ ...zionFinalSelfieWithSamanthaPhoto, wide: true }],
        },
      ],
    };
  }

  return section;
});

const zionStoryPages = [
  {
    slug: 'overview',
    label: 'Overview',
    title: 'Zion National Park',
    date: 'November 30 – December 1, 2024',
    summary: 'Two days in Zion turned a bucket-list destination into countless memories for the Reckon crew.',
    sections: [
      {
        heading: 'Zion National Park',
        subheading: 'Two Days. Countless Memories.',
        date: 'November 30 – December 1, 2024',
        paragraphs: copyBetween('November 30 – December 1, 2024', 'Day One'),
      },
    ],
  },
  {
    slug: 'day-1',
    label: 'Day 1',
    title: 'The Road to Zion',
    date: 'November 30, 2024',
    summary: 'The road from Las Vegas, dinner in Springdale, and a quiet night beneath Zion\'s stars made the journey part of the adventure.',
    sections: dayOneSections,
  },
  {
    slug: 'day-2',
    label: 'Day 2',
    title: 'Three Hikes. One Unforgettable Day.',
    date: 'December 1, 2024',
    summary: 'Scout Lookout, Riverside Walk, and Watchman Trail revealed three completely different sides of Zion in a single, unforgettable day.',
    sections: dayTwoSections,
  },
];

export default zionStoryPages;
